import React, { useEffect, useRef, useState } from "react";
import Icon from "../../../../components/Icon";

import InputSelect from "../../../../components/InputSelect";
import Input from "../../../../components/Input";
import InputRadio from "../../../../components/InputRadio";
import { createOptions } from '../../../../helpers/selects';
import { convertIntToMoney, convertMoenyToInt } from "../../../../helpers/conversions";
import Button from "../../../../components/Button";
import { calculateCustomizationTotal, calculateSizeUnits, calculateSizeCustomizationTotal } from "../customizationCalculate";
import { canSeeCustomizationCost, canSeeCustomizationSupplier } from "../../../../services/auth";
import { productHasSpotComponents, spotAreaLabel } from '../spotComponents';
import { getSomarcasEngravingPrice } from '../somarcasEngraving';
import './styles.scss';

const calculationTypeOptions = [
    { label: 'Pré-cadastrado', value: 'automatic' },
    { label: 'Manual', value: 'manual' }
];

const supplierSelectionModeOptions = [
    { label: 'Pelo menor preço', value: 'automatic' },
    { label: 'Manualmente', value: 'manual' }
];

// Destaque das técnicas compatíveis com o produto (paleta em assets/scss/_conf.scss).
const recommendedSelectStyles = {
    option: (provided, state) => {
        if (!state.data?.recommended) return provided;
        return {
            ...provided,
            borderLeft: '3px solid #362B7A',
            backgroundColor: state.isFocused || state.isSelected ? provided.backgroundColor : '#D5E2F4'
        };
    }
};

const formatCustomizationOptionLabel = item => (
    <div className="select-label">
        <span>{item.label}</span>
        {item?.recommended &&
            <span className="recommended-badge">Recomendada</span>
        }
    </div>
);

const CustomizationSection = ({
    index,
    product,
    customizations,
    productCustomization,
    productCustomizations,
    setProductCustomizations,
    productQuantity,
    productSupplierCost
}) => {
    const [suppliers, setSuppliers] = useState([]);
    const [calculationPer, setCalculationPer] = useState('unit');
    const [customizationUnitCostError, setCustomizationUnitCostError] = useState('');
    const [customizationTotalCostError, setCustomizationTotalCostError] = useState('');

    const [tableOptions, setTableOptions] = useState([{ label: 'Tabela pré-cadastrada', value: 'registered' }]);

    useEffect(() => {
        if(product){
            getTableOptions();
            checkSpot();
        }
    }, [product]);

    const checkSpot = () => {
        if (productCustomization.table !== 'spot') return;
        if (!Array.isArray(product?.spot_components) || product.spot_components.length === 0) return;

        let customizations = getCustomizations();
        if(productCustomization.spot_customization && customizations.filter(customization => customization.CustomizationTypeName === productCustomization.spot_customization ).length === 0) {
            deleteCustomization();
        }
    }

    const getTableOptions = () => {
        if (productHasSpotComponents(product)) {
            setTableOptions(prev => [prev[0],  { label: 'Tabela fornecedor do produto', value: 'spot' }]);
            return;
        }

        if (product.origin === 'integracao_somarcas' && (product.somarcas_engraving_price ?? 0) > 0) {
            setTableOptions(prev => [prev[0], { label: 'Tabela fornecedor do produto', value: 'somarcas' }]);
        }
    }

    useEffect(() => {
        getSuppliers();
        getCalculationPer();
    }, [customizations,productCustomization.selectedCustomization]);

    useEffect(() => {
        handleQuantityChange(productQuantity);
    }, [productQuantity]);

    useEffect(() => {
        getCustomizationUnitCost();
    },
    [
        productCustomization.calculationType,
        productCustomization.selectedSupplierCustomization,
        productCustomization.selectedCustomization,
        productCustomization.quantity,
        productCustomization.quantityColors,
        productCustomization.supplierSelectionMode,
        productCustomization.artWidthMm,
        productCustomization.artHeightMm,
        productCustomization.spacingMm,
        productCustomization.engravingsPerItem,
        productCustomization.selectedSizeId,
        suppliers
    ]);

    useEffect(() => {
        calculateCustomizationTotalCost();
    }, [
        product,
        productCustomization.table,
        productCustomization.spot_component,
        productCustomization.spot_location,
        productCustomization.spot_area,
        productCustomization.spot_customization,
        productCustomization.spot_colors,
        productCustomization.quantity,
        productCustomization.quantityColors,
        productCustomization.customizationUnitCost,
        productCustomization.customizationUnitCostType,
        productCustomization.units,
        productCustomization.fixedFee,
        productCustomization.applicationCost,
        productCustomization.selectedSizeId
    ]);

    // Modo "size": pré-preenche espaçamento (default da gravação) e gravações por item
    useEffect(() => {
        if(calculationPer !== 'size') return;
        const customization = getSelectedCustomizationObject();
        if(!customization) return;
        if(productCustomization.spacingMm === null || productCustomization.spacingMm === undefined){
            handleParamChange('spacingMm', customization.default_spacing_mm ?? 0);
        }
        if(productCustomization.engravingsPerItem === null || productCustomization.engravingsPerItem === undefined){
            handleParamChange('engravingsPerItem', 1);
        }

        // gravação com uma única medida: já vem selecionada
        const sizes = Array.isArray(customization.sizes) ? customization.sizes : [];
        if(sizes.length === 1 && !productCustomization.selectedSizeId){
            handleParamChange('selectedSizeId', sizes[0].id);
        }
    }, [calculationPer, productCustomization.selectedCustomization]);

    const zeroUnitCost = () => {
        handleCustomizationUnitCostChange( `R$ 0,00` );
    }

    const getSelectedCustomizationObject = () => {
        const found = (Array.isArray(customizations) ? customizations : []).filter(c => c.id === productCustomization.selectedCustomization);
        return found.length > 0 ? found[0] : null;
    }

    const getCompatibleCustomizationIds = () => {
        return Array.isArray(product?.customization_ids) ? product.customization_ids : [];
    }

    const getRegisteredCustomizationOptions = () => {
        const compatibleIds = getCompatibleCustomizationIds();
        const options = (Array.isArray(customizations) ? customizations : []).map(c => ({
            label: c.name,
            value: c.id,
            recommended: compatibleIds.includes(c.id)
        }));
        // Compatíveis no topo; ordenação original preservada dentro de cada grupo (sort estável).
        options.sort((a, b) => (b.recommended === true) - (a.recommended === true));
        return [{ value: null, label: 'Nenhuma opção', recommended: false }, ...options];
    }

    // Pré-seleciona automaticamente quando o produto tiver só uma técnica compatível.
    // Avalia uma única vez por produto (ref) para não brigar com a escolha manual do usuário
    // (ex.: ao selecionar "Nenhuma opção").
    const autoSelectedProductRef = useRef(null);
    useEffect(() => {
        if (productCustomization.table !== 'registered') return;
        if (productCustomization.deleted) return;
        if (!product?.id) return;
        if (!Array.isArray(customizations) || customizations.length === 0) return; // aguarda a lista carregar
        if (autoSelectedProductRef.current === product.id) return; // já avaliado para este produto
        autoSelectedProductRef.current = product.id;
        if (productCustomization.selectedCustomization) return; // não sobrescreve seleção existente
        const available = getCompatibleCustomizationIds().filter(id => customizations.some(c => c.id === id));
        if (available.length === 1) {
            handleSelectedCustomizationChange(available[0]);
        }
    }, [product, customizations, productCustomization.table, productCustomization.selectedCustomization, productCustomization.deleted]);

    const getSuppliers = () => {
        let toSuppliers = [];
        let customization = customizations.filter(c => c.id === productCustomization.selectedCustomization);

        if(customization.length > 0){
            customization = customization[0];
            customization.companies.map(company => {
                const { id, customization_id, ...filteredPivot } = company.pivot;
                toSuppliers.push({
                    id: company.id,
                    name: company.trade_name,
                    type: "PJ",
                    ...filteredPivot
                });
            });
            customization.customers.map(customer => {
                const { id, customization_id, ...filteredPivot } = customer.pivot;
                toSuppliers.push({
                    id: customer.id,
                    name: customer.name,
                    type: "PF",
                    ...filteredPivot
                });
            });
        }

        setSuppliers(toSuppliers);
    }

    const calculateSpotCustomizationTotalCost = () => {
        let error = 'Preço não encontrado.';

        let scalePrices = getScalePrices();

        let total = 0;
        if(scalePrices.length > 0){
            scalePrices.map(scalePrice => {
                if(scalePrice.MinQt <= productQuantity){
                    total = scalePrice.Price * productQuantity * 100
                    error = '';
                }
            });
        }

        // O ScalePrices do productsTree vem com o produto somado; o composto pelo
        // backend (BDM-1154) traz só a gravação e avisa pelo marcador.
        const scalePricesIncludeProduct = product?.spot_prices_include_product !== false;
        if(total > 0 && scalePricesIncludeProduct){
            total = total - (productSupplierCost * productQuantity);
        }

        setCustomizationTotalCostError(error);
        handleCustomizationTotalCostChange( `R$ ${convertIntToMoney(total)}`);
    }

    const calculateSomarcasCustomizationTotalCost = () => {
        const { price: unitPrice, found } = getSomarcasEngravingPrice(product, productQuantity);
        const total = unitPrice * productQuantity;

        setCustomizationTotalCostError(found ? '' : 'Gravação não encontrada no fornecedor para a quantidade informada.');
        handleCustomizationTotalCostChange( `R$ ${convertIntToMoney(total)}`);
    }

    const calculateCustomizationTotalCost = () => {
        if(productCustomization.table === 'spot'){
            calculateSpotCustomizationTotalCost();
            return;
        }
        if(productCustomization.table === 'somarcas'){
            calculateSomarcasCustomizationTotalCost();
            return;
        }

        // Modo "size": total = unidades de impressão × custo por unidade + taxa fixa
        // + aplicação por peça (não multiplica por cores)
        if(getCalculationPer() === 'size'){
            const units = productCustomization.units;
            const total = units
                ? calculateSizeCustomizationTotal(units, convertMoenyToInt(productCustomization.customizationUnitCost), productCustomization.fixedFee || 0, productCustomization.applicationCost || 0, productCustomization.quantity || 0)
                : 0;
            handleCustomizationTotalCostChange( `R$ ${convertIntToMoney(total)}`);
            return;
        }

        let total = calculateCustomizationTotal(
            productCustomization.quantity,
            convertMoenyToInt( productCustomization.customizationUnitCost),
            getCalculationPer(),
            productCustomization.customizationUnitCostType
        );
        let customization = customizations.filter(c => c.id === productCustomization.selectedCustomization);
        if(customization.length > 0){
            customization = customization[0];
            if(customization.multiply_colors){
                total = total * productCustomization.quantityColors;
            }
        }


        handleCustomizationTotalCostChange( `R$ ${convertIntToMoney(total)}`);
    }

    const handleTableChange = value => {
        handleParamChange('table', value);
    }

    const handleSpotComponentChange = value => {
        handleParamChange('spot_component', value);
    }

    const handleSpotLocationChange = value => {
        handleParamChange('spot_location', value);
    }

    const handleSpotAreaChange = value => {
        handleParamChange('spot_area', value);
    }

    const handleSpotCustomizationChange = value => {
        handleParamChange('spot_customization', value);
    }

    const handleSpotColorsChange = value => {
        console.log('handleSpotColorsChange', value);
        handleParamChange('spot_colors', value);
        if(!value){
            handleQuantityColorsChange(0);
            return;
        }

        handleQuantityColorsChange(value.endsWith('F') ? 9999 : parseInt(value.split('-').pop(), 10));
    }

    const handleCalculationTypeChange = value => {
        handleParamChange('calculationType', value);
    }

    const handleSupplierSelectionModeChange = value => {
        handleParamChange('supplierSelectionMode', value);
    }

    const handleSelectedCustomizationChange = value => {
        if(value !== productCustomization.selectedCustomization){
            handleSelectedSupplierCustomizationChange(null);
            zeroUnitCost();
            // medida/espaçamento pertencem à gravação anterior
            handleParamChange('selectedSizeId', null);
            handleParamChange('units', null);
            handleParamChange('fixedFee', null);
            handleParamChange('applicationCost', null);
            handleParamChange('spacingMm', null);
        }
        handleParamChange('selectedCustomization', value);
    }

    const handleSelectedSupplierCustomizationChange = value => {
        let supplier = getSupplier(value);
        handleParamChange('selectedSupplierTypeCustomization', supplier ? supplier.type : null);
        // zeroUnitCost();
        handleParamChange('selectedSupplierCustomization', value);
    }

    const handleQuantityColorsChange = value => {
        handleParamChange('quantityColors', value);
    }

    const handleQuantityChange = value => {
        handleParamChange('quantity', value);
    }

    const handleCustomizationUnitCostChange = value => {
        handleParamChange('customizationUnitCost', value);
    }

    const handleCustomizationUnitCostTypeChange = value => {
        handleParamChange('customizationUnitCostType', value);
    }

    const handleCustomizationTotalCostChange = value => {
        handleParamChange('customizationTotalCost', value);
    }

    const handleArtWidthChange = value => {
        handleParamChange('artWidthMm', value);
    }

    const handleArtHeightChange = value => {
        handleParamChange('artHeightMm', value);
    }

    const handleEngravingsPerItemChange = value => {
        handleParamChange('engravingsPerItem', value);
    }

    const handleSpacingChange = value => {
        handleParamChange('spacingMm', value);
    }

    const handleSelectedSizeChange = value => {
        handleParamChange('selectedSizeId', value);
    }

    const getCalculationPer = () => {
        let toCalculationPer = 'unit';
        let customization = customizations.filter(c => c.id === productCustomization.selectedCustomization);
        
        if(customization.length > 0){
            customization = customization[0];
            toCalculationPer = customization.calculation_per;
        }

        setCalculationPer(toCalculationPer);
        return toCalculationPer;
    }

    const handleParamChange = (param, value) => {
        setProductCustomizations(prev => {
            let toProductCustomizations = [];

            prev.map((c, i) => {
                if(i === index){
                    let updated = {...c};
                    updated[param] = value;
                    toProductCustomizations.push(updated);
                } else {
                    toProductCustomizations.push(c);
                }
            });

            return toProductCustomizations;
        });
    }

    const getSupplier = id => {
        let supplier = suppliers.filter(s => s.id === id);
        return supplier.length > 0 ? supplier[0] : null;
    }

    // Modo "size": custo depende da geometria (arte x medida) e das faixas por unidades consumidas
    const getSizeCustomizationCost = customization => {
        const sizes = Array.isArray(customization.sizes) ? customization.sizes : [];
        const artW = productCustomization.artWidthMm;
        const artH = productCustomization.artHeightMm;
        const spacing = productCustomization.spacingMm ?? customization.default_spacing_mm ?? 0;
        const engravingsPerItem = productCustomization.engravingsPerItem || 1;
        const totalArts = (productCustomization.quantity || 0) * engravingsPerItem;

        if(!artW || !artH || !totalArts){
            handleParamChange('units', null);
            return; // aguarda as dimensões da arte
        }

        // Origem do custo manual: só calcula as unidades da medida escolhida
        if(productCustomization.calculationType === 'manual'){
            const size = sizes.find(s => s.id === productCustomization.selectedSizeId) ?? null;
            const units = size ? calculateSizeUnits(size, artW, artH, spacing, totalArts) : null;
            handleParamChange('units', units);
            if(size && units === null){
                setCustomizationUnitCostError('A arte não cabe na medida selecionada');
            }
            return;
        }

        if(suppliers.length === 0) return;

        // Agrupa as linhas de preço por fornecedor + medida
        const groups = {};
        suppliers.forEach(price => {
            if(price.customization_size_id === null || price.customization_size_id === undefined) return;
            const key = `${price.type}-${price.id}-${price.customization_size_id}`;
            if(!groups[key]){
                groups[key] = {
                    supplierId: price.id,
                    supplierType: price.type,
                    sizeId: price.customization_size_id,
                    fixedFee: price.fixed_fee ?? 0,
                    applicationCost: price.application_cost ?? 0,
                    rows: []
                };
            }
            groups[key].rows.push(price);
        });

        let combos = [];
        const anySizeFits = sizes.some(size => calculateSizeUnits(size, artW, artH, spacing, totalArts) !== null);

        Object.values(groups).forEach(group => {
            // fornecedor manual: só considera o escolhido (nenhum escolhido = nenhum candidato)
            if(
                productCustomization.supplierSelectionMode === 'manual' &&
                group.supplierId !== productCustomization.selectedSupplierCustomization
            ) return;

            const size = sizes.find(s => s.id === group.sizeId);
            if(!size) return;

            const units = calculateSizeUnits(size, artW, artH, spacing, totalArts);
            if(units === null) return;

            const band = group.rows.find(row => row.init_qtd <= units && (row.final_qtd === null || units <= row.final_qtd));
            if(!band) return;

            const fixedFee = band.fixed_fee ?? group.fixedFee ?? 0;
            const applicationCost = band.application_cost ?? group.applicationCost ?? 0;
            combos.push({
                supplierId: group.supplierId,
                supplierType: group.supplierType,
                sizeId: group.sizeId,
                units,
                cost: band.cost,
                fixedFee,
                applicationCost,
                total: calculateSizeCustomizationTotal(units, band.cost, fixedFee, applicationCost, productCustomization.quantity || 0)
            });
        });

        // Medida escolhida manualmente pelo vendedor tem prioridade
        let candidates = combos;
        if(productCustomization.selectedSizeId !== null && productCustomization.selectedSizeId !== undefined){
            candidates = combos.filter(combo => combo.sizeId === productCustomization.selectedSizeId);
        }

        if(candidates.length === 0){
            handleParamChange('units', null);
            handleCustomizationUnitCostChange( `R$ 0,00` );

            let error = 'Preço do fornecedor não encontrado.';
            if(!anySizeFits){
                error = 'A arte não cabe em nenhuma medida cadastrada';
            } else if(productCustomization.selectedSizeId !== null && productCustomization.selectedSizeId !== undefined){
                const selectedSize = sizes.find(s => s.id === productCustomization.selectedSizeId);
                if(!selectedSize || calculateSizeUnits(selectedSize, artW, artH, spacing, totalArts) === null){
                    error = 'A arte não cabe na medida selecionada';
                }
            }
            setCustomizationUnitCostError(error);
            return;
        }

        const chosen = candidates.sort((a, b) => a.total - b.total)[0];

        if(productCustomization.supplierSelectionMode === 'automatic' && chosen.supplierId !== productCustomization.selectedSupplierCustomization){
            handleSelectedSupplierCustomizationChange(chosen.supplierId);
        }
        if(chosen.sizeId !== productCustomization.selectedSizeId){
            handleParamChange('selectedSizeId', chosen.sizeId);
        }

        handleParamChange('units', chosen.units);
        handleParamChange('fixedFee', chosen.fixedFee);
        handleParamChange('applicationCost', chosen.applicationCost);
        handleCustomizationUnitCostChange( `R$ ${convertIntToMoney(chosen.cost)}` );
        handleCustomizationUnitCostTypeChange(null);
    }

    const getCustomizationUnitCost = () => {
        setCustomizationUnitCostError('');

        let sizeCustomization = getSelectedCustomizationObject();
        if(sizeCustomization && sizeCustomization.calculation_per === 'size'){
            getSizeCustomizationCost(sizeCustomization);
            return;
        }

        if(productCustomization.calculationType === 'manual') return;

        let cost = 0;
        let type = null;
        let error = 'Preço do fornecedor não encontrado.';
        let prices = [];
        const quantity = productCustomization.quantity;
        let thousands = quantity / 1000;
        thousands = Math.ceil(thousands);

        let customization = customizations.filter(c => c.id === productCustomization.selectedCustomization);

        if(customization.length === 0 || suppliers.length === 0) return;

        customization = customization[0];

        prices = sortPrices(suppliers);

        let selectedPrices = [];

        prices.map(price => {
            if(customization.calculation_per === 'unit' || customization.calculation_per === 'interval'){
                if(price.init_qtd <= quantity && price.final_qtd >= quantity){
                    selectedPrices.push(price);
                }
            } else if(price.thousand === thousands) {
                selectedPrices.push(price);
            }
        });    

        selectedPrices.map((selectedPrice, index) => {
            let companyId = productCustomization?.selectedSupplierCustomization ?? null;

            if(index === 0 && productCustomization.supplierSelectionMode === 'automatic'){
                companyId = selectedPrice.company_id;
                handleSelectedSupplierCustomizationChange(companyId);
            }

            if(selectedPrice.company_id === companyId){
                type = selectedPrice.cost_type;
                cost = selectedPrice.cost;
                error = '';
            }
        });

        handleCustomizationUnitCostChange( `R$ ${convertIntToMoney(cost)}` );
        handleCustomizationUnitCostTypeChange(type);
        setCustomizationUnitCostError(error);
    }
    
    const sortPrices = prices => {
        return prices.sort((a, b) => a.cost - b.cost);
    }

    const deleteCustomization = () => {
        let toProductCustomizations = [];
        productCustomizations.map((productCustomization, i) => {
            if(i !== index){
                toProductCustomizations.push(productCustomization);
            }
        });

        setProductCustomizations([...toProductCustomizations]);
    }

    const getLocations = () => {
        if(!product) return [];
        const components = Array.isArray(product.spot_components) ? product.spot_components : [];
        let component = components.find(component => component.Name === productCustomization.spot_component);
        if(!component) return [];
        
        let options = [];

        (component.Locations || []).map(location => {
            options.push({
                ...location,
                value: location.Name,
                label: location.Name
            });
        })
        
        return options;
    }

    const getCustomizations = () => {
        if(!product) return [];
        let locations = getLocations();
        let location = locations.find(location => location.Name === productCustomization.spot_location);
        if(!location) return [];

        let options = [];
        let keys = [];

        (location.CustomizationAreas || []).map(customizationArea => {
            (customizationArea.ProductCustomizationTables || []).map(productCustomizationTable => {
                let key = productCustomizationTable.CustomizationTypeName;
                if(!keys.includes(key)){
                    options.push({
                        ...productCustomizationTable,
                        value: key,
                        label: key
                    });
                    keys.push(key);
                }
            })
        });

        return options;
    }

    const getAreas = () => {
        if(!product) return [];

        let locations = getLocations();
        
        if(!locations) return [];

        let options = [];
        let values = [];

        locations.map(location => {
            if(location.Name !== productCustomization.spot_location) return;

            (location.CustomizationAreas || []).map(customizationArea => {
                (customizationArea.ProductCustomizationTables || []).map(productCustomizationTable => {
                    if(productCustomizationTable.CustomizationTypeName === productCustomization.spot_customization){
                        (productCustomizationTable.ProductCustomizationTableOptions || []).map(productCustomizationTableOption => {
                            let value = productCustomizationTableOption.AreaCM;
                            let label = spotAreaLabel(productCustomizationTableOption);
                            if(!values.includes(value)){
                                options.push({
                                    value,
                                    label
                                });
                                values.push(value);
                            }
                        });
                    }
                });
            });
        });

        // Faixas maiores que o local recortam para o mesmo tamanho de impressão
        // e viram itens iguais com preços diferentes. Fica a primeira, que é a
        // faixa menor, salvo a que o orçamento já tem gravada: sem ela o select
        // reabriria vazio.
        const kept = new Map();
        options.forEach(option => {
            if(!kept.has(option.label) || option.value === productCustomization.spot_area){
                kept.set(option.label, option);
            }
        });

        return options.filter(option => kept.get(option.label) === option);
    }

    const getColors = () => {
        if(!product) return [];

        let locations = getLocations();
        
        if(!locations) return [];

        let options = [];
        let values = [];

        locations.map(location => {
            if(location.Name !== productCustomization.spot_location) return;

            (location.CustomizationAreas || []).map(customizationArea => {
                (customizationArea.ProductCustomizationTables || []).map(productCustomizationTable => {
                    if(productCustomizationTable.CustomizationTypeName === productCustomization.spot_customization){
                        (productCustomizationTable.ProductCustomizationTableOptions || []).map(productCustomizationTableOption => {
                            if(productCustomizationTableOption.AreaCM === productCustomization.spot_area){
                                let colors = productCustomizationTableOption.Colors;
                                let value = productCustomizationTableOption.TableCodeOption;
                                let isFullColor = productCustomizationTableOption.TableCodeOption.endsWith('F');
                                let label = isFullColor ? 'Full colour' : `${colors} cor${colors > 1 ? 'es' : ''}`;

                                options.push({
                                    value,
                                    label
                                });
                            }
                        });
                    }
                });
            });
        });

        return options;
    }

    const getSizeOptions = () => {
        const customization = getSelectedCustomizationObject();
        const sizes = Array.isArray(customization?.sizes) ? customization.sizes : [];
        return sizes.map(size => ({
            label: `${size.name} (${size.width_mm}x${size.height_mm}mm)`,
            value: size.id
        }));
    }

    const getSizeBreakdown = () => {
        if(calculationPer !== 'size') return null;
        const customization = getSelectedCustomizationObject();
        if(!customization) return null;
        const sizes = Array.isArray(customization.sizes) ? customization.sizes : [];
        const size = sizes.find(s => s.id === productCustomization.selectedSizeId);
        const units = productCustomization.units;
        if(!size || !units) return null;
        const cost = convertMoenyToInt(productCustomization.customizationUnitCost);
        if(!cost) return null;
        const fixedFee = productCustomization.fixedFee || 0;
        const applicationCost = productCustomization.applicationCost || 0;
        const quantity = productCustomization.quantity || 0;
        const total = calculateSizeCustomizationTotal(units, cost, fixedFee, applicationCost, quantity);
        const unitLabel = size.type === 'roll' ? 'metro(s)' : 'folha(s)';
        const applicationText = applicationCost ? ` + aplicação ${quantity} × R$ ${convertIntToMoney(applicationCost)}` : '';
        return `${units} ${unitLabel} × R$ ${convertIntToMoney(cost)} + taxa fixa R$ ${convertIntToMoney(fixedFee)}${applicationText} = R$ ${convertIntToMoney(total)}`;
    }

    const getScalePrices = () => {
        if(!product) return [];

        let locations = getLocations();
        
        if(!locations) return [];

        let scalePrices = [];
        let tableCodeOption = productCustomization.spot_colors;

        if(!tableCodeOption) return [];

        let tableCodeOptionAlternative = tableCodeOption;

        if (tableCodeOption.endsWith('F')) {
            tableCodeOptionAlternative = tableCodeOption.slice(0, -1) + '04';
        }

        console.log('tableCodeOption', tableCodeOption);

        locations.map(location => {
            if(location.Name !== productCustomization.spot_location) return;

            (location.CustomizationAreas || []).map(customizationArea => {
                (customizationArea.ProductCustomizationTables || []).map(productCustomizationTable => {
                    if(productCustomizationTable.CustomizationTypeName === productCustomization.spot_customization){
                        (productCustomizationTable.ProductCustomizationTableOptions || []).map(productCustomizationTableOption => {
                            if(productCustomizationTableOption.TableCodeOption === tableCodeOption || productCustomizationTableOption.TableCodeOption === tableCodeOptionAlternative){
                                scalePrices = productCustomizationTableOption.ScalePrices;
                            }
                        });
                    }
                });
            });
        });

        return scalePrices;
        
    }

    return (
        <>
            <div className="modal-section-title">
                <div className="left">
                    <h2>Gravação</h2>
                    <Button type={`transparent`} size={`small`} svg={`delete-small`} action={deleteCustomization} />
                </div>
                <div className="right">
                    {tableOptions.length > 1 &&
                        <InputRadio
                            className={`no-margin`}
                            options={tableOptions}
                            value={productCustomization.table}
                            change={handleTableChange}
                        />
                    }
                </div>
            </div>

            <div style={{ position: 'relative' }}>
                {productCustomization.deleted && (
                    <div className="deleted-overlay">
                        <div className="deleted-content">
                            <Icon name="octagon-alert" className="deleted-icon" size={48} color="#F59E0B" />

                            <span className="deleted-title">Gravação excluída</span>
                            <span className="deleted-description">Essa gravação foi excluída e não pode mais ser editada.</span>
                        </div>
                    </div>
                )}

            {product && productCustomization.table === 'spot' &&
                <div className="section">
                    <div className="row">
                        <div className="col-4">
                            <InputSelect
                                label={`Componente`}
                                options={createOptions(Array.isArray(product.spot_components) ? product.spot_components : [], 'Name', 'Name')}
                                value={productCustomization.spot_component}
                                change={handleSpotComponentChange}
                            />
                        </div>
                        <div className="col-4">
                            {productCustomization.spot_component &&
                                <InputSelect
                                    label={`Localização`}
                                    options={getLocations()}
                                    value={productCustomization.spot_location}
                                    change={handleSpotLocationChange}
                                />
                            }
                        </div>
                        <div className="col-4">
                            {productCustomization.spot_location &&
                                <InputSelect
                                    label={`Técnica`}
                                    options={getCustomizations()}
                                    value={productCustomization.spot_customization}
                                    change={handleSpotCustomizationChange}
                                />
                                
                            }
                        </div>
                        <div className="col-4">
                            {productCustomization.spot_customization && 
                                <InputSelect
                                    label={`Área da gravação`}
                                    options={getAreas()}
                                    value={productCustomization.spot_area}
                                    change={handleSpotAreaChange}
                                />
                            }
                        </div>
                        <div className="col-4">
                            {productCustomization.spot_area &&
                                <InputSelect
                                    label={`Num. de cores`}
                                    value={productCustomization.spot_colors}
                                    change={handleSpotColorsChange}
                                    options={getColors()}
                                />
                            }
                        </div>
                        <div className="col-4">
                            {productCustomization.spot_customization && canSeeCustomizationCost() &&
                                <Input
                                    label={`Total`}
                                    value={productCustomization.customizationTotalCost}
                                    disabled={true}
                                    error={customizationTotalCostError}
                                />
                            }
                        </div>
                    </div>
                </div>
            }

            {product && productCustomization.table === 'somarcas' &&
                <div className="section">
                    <div className="row">
                        <div className="col-12">
                            <Input
                                label={`Tipo de gravação`}
                                value={product.somarcas_engraving_type || ''}
                                disabled={true}
                            />
                        </div>
                    </div>
                    {canSeeCustomizationCost() &&
                        <div className="row">
                            <div className="col-6">
                                <Input
                                    label={`Valor unitário por gravação`}
                                    value={`R$ ${convertIntToMoney(getSomarcasEngravingPrice(product, productQuantity).price)}`}
                                    disabled={true}
                                />
                            </div>
                            <div className="col-6">
                                <Input
                                    label={`Valor total`}
                                    value={productCustomization.customizationTotalCost}
                                    disabled={true}
                                    error={customizationTotalCostError}
                                />
                            </div>
                        </div>
                    }
                </div>
            }

            {productCustomization.table === 'registered' &&
                <div className={`section ${productCustomization.deleted ? 'disabled-section' : ''}`}>
                    <div className="row">
                        <div className="col-4">
                            {productCustomization.deleted ? (
                                <Input
                                    label={`Gravação`}
                                    value={productCustomization.customizationName || 'Gravação removida'}
                                    disabled={true}
                                />
                            ) : (
                                <InputSelect
                                    options={getRegisteredCustomizationOptions()}
                                    value={productCustomization.selectedCustomization}
                                    change={handleSelectedCustomizationChange}
                                    label={`Gravação`}
                                    formatOptionLabel={formatCustomizationOptionLabel}
                                    styles={recommendedSelectStyles}
                                />
                            )}
                        </div>
                        {canSeeCustomizationSupplier() &&
                            <>
                                <div className="col-4">
                                    <InputSelect
                                        label={`Escolha do fornecedor`}
                                        options={supplierSelectionModeOptions}
                                        value={productCustomization.supplierSelectionMode}
                                        change={handleSupplierSelectionModeChange}
                                        isDisabled={productCustomization.deleted}
                                    />
                                </div>
                                <div className="col-4">
                                    {productCustomization.deleted ? (
                                        <Input
                                            label={`Fornecedor`}
                                            value={productCustomization.company || 'Fornecedor não disponível'}
                                            disabled={true}
                                        />
                                    ) : (
                                        <InputSelect 
                                            label={`Fornecedor`}
                                            isDisabled={productCustomization.supplierSelectionMode === 'automatic'}
                                            options={createOptions(suppliers, 'name', 'id', true)}
                                            value={productCustomization.selectedSupplierCustomization}
                                            change={handleSelectedSupplierCustomizationChange}
                                        />
                                    )}
                                </div>
                            </>
                        }
                        {calculationPer !== 'size' &&
                            <div className="col-3">
                                <Input
                                    label={`Num. de cores`}
                                    value={productCustomization.quantityColors}
                                    change={handleQuantityColorsChange}
                                    mask={`convertToInt`}
                                    disabled={productCustomization.deleted}
                                />
                            </div>
                        }
                        {calculationPer === 'size' &&
                            <>
                                <div className="col-3">
                                    <Input
                                        label={`Largura da arte (mm)`}
                                        value={productCustomization.artWidthMm}
                                        change={handleArtWidthChange}
                                        mask={`convertToInt`}
                                        disabled={productCustomization.deleted}
                                    />
                                </div>
                                <div className="col-3">
                                    <Input
                                        label={`Altura da arte (mm)`}
                                        value={productCustomization.artHeightMm}
                                        change={handleArtHeightChange}
                                        mask={`convertToInt`}
                                        disabled={productCustomization.deleted}
                                    />
                                </div>
                                <div className="col-3">
                                    <Input
                                        label={`Gravações por item`}
                                        value={productCustomization.engravingsPerItem}
                                        change={handleEngravingsPerItemChange}
                                        mask={`convertToInt`}
                                        disabled={productCustomization.deleted}
                                    />
                                </div>
                                <div className="col-3">
                                    <Input
                                        label={`Espaçamento (mm)`}
                                        value={productCustomization.spacingMm}
                                        change={handleSpacingChange}
                                        mask={`convertToInt`}
                                        disabled={productCustomization.deleted}
                                    />
                                </div>
                                <div className="col-3">
                                    <InputSelect
                                        label={`Medida`}
                                        options={getSizeOptions()}
                                        value={productCustomization.selectedSizeId}
                                        change={handleSelectedSizeChange}
                                        isDisabled={productCustomization.deleted}
                                    />
                                </div>
                            </>
                        }
                        {canSeeCustomizationCost() &&
                            <>
                                <div className="col-3">
                                    <InputSelect
                                        label={`Origem do custo`}
                                        options={calculationTypeOptions}
                                        value={productCustomization.calculationType}
                                        change={handleCalculationTypeChange}
                                        isDisabled={productCustomization.deleted}
                                    />
                                </div>
                                <div className="col-3">
                                    <Input
                                        label={calculationPer === 'thousand' ? 'Custo milheiro' : calculationPer === 'size' ? 'Custo por unidade' : 'Custo'}
                                        // label={calculationPer === 'unit' ? 'Custo unit.' : 'Custo milheiro'}
                                        value={productCustomization.customizationUnitCost}
                                        change={handleCustomizationUnitCostChange}
                                        mask={`convertToMoney`}
                                        disabled={productCustomization.calculationType === 'automatic' || productCustomization.deleted}
                                        error={calculationPer === 'size' ? '' : customizationUnitCostError}
                                    />
                                </div>
                                <div className="col-3">
                                    <Input
                                        label={`Total`}
                                        value={productCustomization.customizationTotalCost}
                                        disabled={true}
                                    />
                                </div>
                            </>
                        }
                    </div>
                    {calculationPer === 'size' && customizationUnitCostError !== '' &&
                        <p className="size-cost-message size-cost-message--error">{customizationUnitCostError}</p>
                    }
                    {calculationPer === 'size' && customizationUnitCostError === '' && canSeeCustomizationCost() && getSizeBreakdown() &&
                        <p className="size-cost-message">{getSizeBreakdown()}</p>
                    }
                </div>
            }
            </div>
        </>
    );
}

export default CustomizationSection;
